import {
    React,
    useState,
    useEffect,
    Drawer,
    DialogActions,
    Box,
    Typography,
    Divider,
    IconButton,
    ArrowBackIosIcon,
    Default_Text_Color
} from '../../../utils/globalImports';

import {
    CustomButton,
    CustomTextField,
    CustomRadioButton,
    validationMessages,
} from '../../../utils/globalImports';

export interface Category {
    id?: number;
    projectId?: number;
    description: string;
    notes: string;
    isActive?: boolean;
    createdid?: number;
    updatedId?: number;
}


interface ModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (formData: Category) => void;
    categoryData?: Category; // Optional prop for editing a user
}

const AddProjectCategory: React.FC<ModalProps> = ({ open, onClose, onSubmit, categoryData }) => {

    const [formData, setFormData] = useState<Category>({
        description: '',
        notes: ''
    });

    const [errors, setErrors] = useState({
        description: '',
        notes: '',
    });

    // Populate fields if editing a category
    useEffect(() => {
        if (open) {
            if (categoryData) {
                setFormData({
                    description: categoryData.description || '',
                    notes: categoryData.notes || ''
                });
            } else {
                setFormData({
                    description: '',
                    notes: ''
                });
            }

            setErrors({
                description: '', notes: ''
            });
        }
    }, [open, categoryData]);


    // Validate input fields
    const validate = () => {
        let isValid = true;
        const errors = { description: '', notes: '' };

        if (!formData.description.trim()) {
            errors.description = validationMessages.common_required("Category Name");
            isValid = false;
        }
        else if (formData.description.length > 255) {
            errors.description = validationMessages.common_characterLimit("Category Name", 255);
            isValid = false;
        }
        else if (!/^[A-Za-z0-9\s-]+$/.test(formData.description)) {
            errors.description = validationMessages.commen_invalidCategoryString("Category Name");
            isValid = false;
        }

        if (formData.notes.length > 5000) {
            errors.notes = validationMessages.common_characterLimit("Note", 5000);
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
        }
    };

    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                width: 350,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 500,
                    height: '100%',
                    paddingLeft: 2,
                    paddingRight: 2,
                    paddingTop: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.86)',
                },
            }}
        >
            <Box>
                <Typography
                    sx={{
                        color: Default_Text_Color,
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 2,
                        mt: 2,
                        position: 'relative',
                        fontWeight: 500,
                        fontSize: 22
                    }}
                >
                    <IconButton
                        sx={{
                            position: 'absolute',
                            left: 0,
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'transparent',
                        }}
                        onClick={onClose}
                    >
                        <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
                    </IconButton>
                    {categoryData ? 'Edit Fee Stage Category' : 'Add Fee Stage Category'}
                </Typography>

                <Divider sx={{ mb: 3, mx: "-15px" }} />

                {/* Client Category Name */}
                <Box sx={{ mb: 2 }}>
                    <CustomTextField
                        label="Category Name"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        sx={{
                            mb: 1,
                            backgroundColor: '#fff'
                        }}
                        required={true}
                    />
                    {errors.description && (
                        <Typography className="validationMessage" role="alert">
                            {errors.description}
                        </Typography>
                    )}
                </Box>

                {/* Description */}
                <Box sx={{ mb: 2 }}>
                    <CustomTextField
                        label="Notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        sx={{
                            mb: 1,
                            backgroundColor: '#fff',
                            '& .MuiInputBase-root': {
                                height: 'auto',
                            },
                            '& .MuiOutlinedInput-input': {
                                minHeight: 100,
                                padding: '10px',
                            },
                        }}
                        required={false}
                        isTextArea={true}
                    />
                    {errors.notes && (
                        <Typography className="validationMessage" role="alert">
                            {errors.notes}
                        </Typography>
                    )}
                </Box>


                {/* Status */}
                {/* <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
                <Box sx={{ display: 'flex' }}>
                    <CustomRadioButton
                        label="Active"
                        value="Active"
                        name="status"
                        checked={formData.status === 'Active'}
                        onChange={handleChange}
                        required={true}
                    />
                    <CustomRadioButton
                        label="Inactive"
                        value="Inactive"
                        name="status"
                        checked={formData.status === 'Inactive'}
                        onChange={handleChange}
                        required={true}
                    />
                </Box> */}

                {/* Submit Button */}
                <DialogActions>
                    <CustomButton
                        capsule
                        sx={{
                            textTransform: 'none',
                            width: '282px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            mr: 'auto',
                            ml: 'auto',
                            mt: 2,
                        }}
                        onClick={handleSubmit}
                    >
                        {categoryData ? 'Update Category' : 'Add Category'}
                    </CustomButton>
                </DialogActions>
            </Box>
        </Drawer>
    );
};

export default AddProjectCategory;
